import React, { useState } from 'react';
import {
    Drawer,
    Typography,
    Space,
    Tag,
    Card,
    Avatar,
    Button,
    Input,
    Select,
    Upload,
    message,
    Tooltip,
    Alert,
    Row,
    Col,
    Descriptions,
    Timeline,
    Modal,
    Spin,
} from 'antd';
import {
    UserOutlined,
    SendOutlined,
    PaperClipOutlined,
    DownloadOutlined,
    DeleteOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
} from '@ant-design/icons';
import {
    useGetAdminTicketQuery,
    useUpdateTicketMutation,
    useAddAdminTicketCommentMutation,
    useUploadAdminTicketAttachmentMutation,
    useDeleteAdminTicketAttachmentMutation,
    useGetAdminUsersQuery,
} from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import type { ITicket, ITicketAttachment } from '../../services/nestjs-base-api/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface TicketDetailDrawerProps {
    ticketHash: string | null;
    visible: boolean;
    onClose: () => void;
}
export const TicketDetailDrawer: React.FC<TicketDetailDrawerProps> = ({
    ticketHash,
    visible,
    onClose,
}) => {
    const { user } = useAuth();
    const [commentText, setCommentText] = useState('');
    const [isInternal, setIsInternal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [statusChangeModalVisible, setStatusChangeModalVisible] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<ITicket['status'] | null>(null);
    const [statusChangeReason, setStatusChangeReason] = useState('');

    // Fetch ticket data
    const {
        data: ticket,
        isLoading,
        refetch,
    } = useGetAdminTicketQuery(
        { jwtToken: user?.jwtToken || '', hash: ticketHash || '' },
        { skip: !ticketHash || !visible },
    );

    // Fetch admin users for assignment
    const { data: adminUsersData } = useGetAdminUsersQuery({
        jwtToken: user?.jwtToken || '',
        limit: 1000,
    });

    // Mutations
    const [updateTicket] = useUpdateTicketMutation();
    const [addComment] = useAddAdminTicketCommentMutation();
    const [uploadAttachment] = useUploadAdminTicketAttachmentMutation();
    const [deleteAttachment] = useDeleteAdminTicketAttachmentMutation();

    // Handle status change
    const handleStatusChange = async (newStatus: ITicket['status']) => {
        if (!ticket) return;

        // For resolved or closed status, show modal for reason
        if (newStatus === 'resolved' || newStatus === 'closed') {
            setPendingStatus(newStatus);
            setStatusChangeReason('');
            setStatusChangeModalVisible(true);
        } else {
            // For other status changes, update directly
            try {
                await updateTicket({
                    jwtToken: user?.jwtToken || '',
                    hash: ticket.hash,
                    data: { status: newStatus },
                }).unwrap();
                message.success('Ticket status updated');
                refetch();
            } catch (error) {
                message.error('Failed to update status');
            }
        }
    };

    // Handle status change confirmation
    const handleStatusChangeConfirm = async () => {
        if (!pendingStatus || !ticket) return;

        if (!statusChangeReason.trim()) {
            message.error('Please provide a reason');
            return;
        }

        try {
            await updateTicket({
                jwtToken: user?.jwtToken || '',
                hash: ticket.hash,
                data: {
                    status: pendingStatus,
                    statusChangeReason: statusChangeReason.trim(),
                },
            }).unwrap();
            message.success(`Ticket ${pendingStatus === 'resolved' ? 'resolved' : 'closed'} successfully`);
            setStatusChangeModalVisible(false);
            setPendingStatus(null);
            setStatusChangeReason('');
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to update status');
        }
    };

    // Handle status change cancel
    const handleStatusChangeCancel = () => {
        setStatusChangeModalVisible(false);
        setPendingStatus(null);
        setStatusChangeReason('');
        refetch(); // Refresh to reset the status dropdown
    };

    // Handle priority change
    const handlePriorityChange = async (newPriority: ITicket['priority']) => {
        if (!ticket) return;

        try {
            await updateTicket({
                jwtToken: user?.jwtToken || '',
                hash: ticket.hash,
                data: { priority: newPriority },
            }).unwrap();
            message.success('Ticket priority updated');
            refetch();
        } catch (error) {
            message.error('Failed to update priority');
        }
    };

    // Handle assignment change
    const handleAssignmentChange = async (userId: number | null) => {
        if (!ticket) return;

        try {
            await updateTicket({
                jwtToken: user?.jwtToken || '',
                hash: ticket.hash,
                data: { assignedToUserId: userId === null ? undefined : userId },
            }).unwrap();
            message.success(userId ? 'Ticket assigned successfully' : 'Ticket unassigned');
            refetch();
        } catch (error) {
            message.error('Failed to update assignment');
        }
    };

    // Handle add comment
    const handleAddComment = async () => {
        if (!ticket || !commentText.trim()) return;

        try {
            await addComment({
                jwtToken: user?.jwtToken || '',
                hash: ticket.hash,
                data: {
                    content: commentText,
                    isInternal,
                },
            }).unwrap();
            message.success('Comment added');
            setCommentText('');
            setIsInternal(false);
            refetch();
        } catch (error) {
            message.error('Failed to add comment');
        }
    };

    // Handle file upload
    const handleFileUpload = async (file: File) => {
        if (!ticket) return false;

        setUploading(true);
        try {
            await uploadAttachment({
                jwtToken: user?.jwtToken || '',
                hash: ticket.hash,
                file,
            }).unwrap();
            message.success('File uploaded successfully');
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to upload file');
        } finally {
            setUploading(false);
        }

        return false; // Prevent default upload behavior
    };

    // Handle delete attachment
    const handleDeleteAttachment = async (attachmentHash: string, filename: string) => {
        if (!ticket) return;

        Modal.confirm({
            title: 'Delete Attachment',
            content: `Are you sure you want to delete "${filename}"?`,
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteAttachment({
                        jwtToken: user?.jwtToken || '',
                        ticketHash: ticket.hash,
                        attachmentHash,
                    }).unwrap();
                    message.success('Attachment deleted');
                    refetch();
                } catch (error) {
                    message.error('Failed to delete attachment');
                }
            },
        });
    };

    // Handle file download
    const handleFileDownload = async (attachmentHash: string, filename: string) => {
        try {
            // Get API base URL and remove trailing slash if present
            let apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3501';
            apiBaseUrl = apiBaseUrl.replace(/\/$/, '');

            const url = `${apiBaseUrl}/admin/tickets/attachments/${attachmentHash}`;

            console.log('API Base URL:', apiBaseUrl);
            console.log('Downloading from:', url);

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${user?.jwtToken}`,
                },
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Download failed:', errorText);
                throw new Error(`Download failed: ${response.status}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);

            message.success('File downloaded successfully');
        } catch (error: any) {
            console.error('Download error:', error);
            message.error(`Failed to download file: ${error.message}`);
        }
    };

    // Render ticket metadata
    const renderTicketInfo = () => {
        if (!ticket) return null;

        return (
            <Card style={{ marginBottom: 16 }}>
                <Descriptions column={2} size="small">
                    <Descriptions.Item label="Ticket ID">
                        <Text code copyable={{ text: ticket.hash }}>
                            {ticket.hash.substring(0, 12)}...
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                        <Tooltip title={dayjs(ticket.createdAt).format('YYYY-MM-DD HH:mm:ss')}>
                            {dayjs(ticket.createdAt).fromNow()}
                        </Tooltip>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created By">
                        <Space>
                            <UserOutlined />
                            {ticket.createdByUser.firstName} {ticket.createdByUser.lastName}
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                        <a href={`mailto:${ticket.createdByUser.email}`}>
                            {ticket.createdByUser.email}
                        </a>
                    </Descriptions.Item>
                    {ticket.organisation && (
                        <Descriptions.Item label="Organisation">
                            <Tag color="purple">{ticket.organisation.name}</Tag>
                        </Descriptions.Item>
                    )}
                    {ticket.brand && (
                        <Descriptions.Item label="Brand">
                            <Tag>{ticket.brand.name}</Tag>
                        </Descriptions.Item>
                    )}
                    {ticket.category && (
                        <Descriptions.Item label="Category">
                            <Tag color={ticket.category.color || 'default'}>
                                {ticket.category.name}
                            </Tag>
                        </Descriptions.Item>
                    )}
                    {ticket.assignedToUser && (
                        <Descriptions.Item label="Assigned To">
                            <Space>
                                <UserOutlined />
                                {ticket.assignedToUser.firstName} {ticket.assignedToUser.lastName}
                            </Space>
                        </Descriptions.Item>
                    )}
                </Descriptions>
            </Card>
        );
    };

    // Render conversation timeline
    const renderConversation = () => {
        if (!ticket || !ticket.comments) return null;

        const allItems = [
            {
                type: 'ticket',
                date: ticket.createdAt,
                user: ticket.createdByUser,
                content: ticket.description,
                isInternal: false,
            },
            ...ticket.comments.map((comment) => ({
                type: 'comment',
                date: comment.createdAt,
                user: comment.authorUser,
                content: comment.content,
                isInternal: comment.isInternal,
            })),
        ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        return (
            <Timeline style={{ marginTop: 16 }}>
                {allItems.map((item, index) => (
                    <Timeline.Item
                        key={index}
                        color={item.type === 'ticket' ? 'blue' : item.isInternal ? 'orange' : 'green'}
                    >
                        <Card
                            size="small"
                            style={{
                                backgroundColor: item.isInternal ? '#fff7e6' : '#fff',
                                border: item.isInternal ? '1px solid #ffa940' : '1px solid #d9d9d9',
                            }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                    <Space>
                                        <Avatar icon={<UserOutlined />} size="small" />
                                        <Text strong>
                                            {item.user.firstName} {item.user.lastName}
                                        </Text>
                                        {item.isInternal && (
                                            <Tag color="orange" icon={<EyeInvisibleOutlined />}>
                                                Internal Note
                                            </Tag>
                                        )}
                                        {item.type === 'ticket' && <Tag color="blue">Original Message</Tag>}
                                    </Space>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {dayjs(item.date).format('MMM D, YYYY HH:mm')}
                                    </Text>
                                </Space>
                                <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                                    {item.content}
                                </Paragraph>
                            </Space>
                        </Card>
                    </Timeline.Item>
                ))}
            </Timeline>
        );
    };

    // Render attachments
    const renderAttachments = () => {
        if (!ticket?.attachments || ticket.attachments.length === 0) return null;

        return (
            <Card title="Attachments" size="small" style={{ marginTop: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {ticket.attachments.map((attachment: ITicketAttachment) => (
                        <Card key={attachment.id} size="small" style={{ backgroundColor: '#fafafa' }}>
                            <Row align="middle" justify="space-between">
                                <Col>
                                    <Space>
                                        <PaperClipOutlined />
                                        <Text>{attachment.originalFilename}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            ({Math.round(attachment.fileSize / 1024)} KB)
                                        </Text>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        <Tooltip title="Download">
                                            <Button
                                                type="text"
                                                icon={<DownloadOutlined />}
                                                onClick={() => handleFileDownload(attachment.hash, attachment.originalFilename)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() =>
                                                    handleDeleteAttachment(
                                                        attachment.hash,
                                                        attachment.originalFilename,
                                                    )
                                                }
                                            />
                                        </Tooltip>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>
                    ))}
                </Space>
            </Card>
        );
    };

    return (
        <Drawer
            title="Ticket Details"
            placement="right"
            width={800}
            onClose={onClose}
            open={visible}
        >
            <Spin spinning={isLoading}>
                {ticket && (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {/* Subject and Actions */}
                    <Card size="small">
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <Title level={4} style={{ margin: 0 }}>
                                {ticket.subject}
                            </Title>
                            <Row gutter={8}>
                                <Col>
                                    <Space direction="vertical" size={2}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>Assign to</Text>
                                        <Select
                                            value={ticket.assignedToUserId || undefined}
                                            onChange={handleAssignmentChange}
                                            placeholder="Assign to..."
                                            allowClear
                                            style={{ width: 200 }}
                                        >
                                            {adminUsersData?.data
                                                ?.filter((adminUser) => {
                                                    // Only show Super Admin and Platform Admin (ADMIN role)
                                                    const roles = typeof adminUser.roles === 'string'
                                                        ? JSON.parse(adminUser.roles || '[]')
                                                        : adminUser.roles || [];
                                                    return roles.includes('SUPER_ADMIN') || roles.includes('ADMIN');
                                                })
                                                .map((adminUser) => (
                                                    <Option key={adminUser.id} value={adminUser.id}>
                                                        {adminUser.firstName && adminUser.lastName
                                                            ? `${adminUser.firstName} ${adminUser.lastName}`
                                                            : adminUser.email}
                                                    </Option>
                                                ))}
                                        </Select>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space direction="vertical" size={2}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>Status</Text>
                                        <Select
                                            value={ticket.status}
                                            onChange={handleStatusChange}
                                            style={{ width: 200 }}
                                        >
                                            <Option value="open">Open</Option>
                                            <Option value="in_progress">In Progress</Option>
                                            <Option value="waiting_on_customer">Waiting on Customer</Option>
                                            <Option value="resolved">Resolved</Option>
                                            <Option value="closed">Closed</Option>
                                        </Select>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space direction="vertical" size={2}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>Priority</Text>
                                        <Select
                                            value={ticket.priority}
                                            onChange={handlePriorityChange}
                                            style={{ width: 140 }}
                                        >
                                            <Option value="low">Low</Option>
                                            <Option value="normal">Normal</Option>
                                            <Option value="high">High</Option>
                                            <Option value="urgent">Urgent</Option>
                                        </Select>
                                    </Space>
                                </Col>
                            </Row>
                        </Space>
                    </Card>

                    {/* Ticket Info */}
                    {renderTicketInfo()}

                    {/* Conversation Thread */}
                    <Card title="Conversation" size="small">
                        {renderConversation()}
                    </Card>

                    {/* Attachments */}
                    {renderAttachments()}

                    {/* Add Comment Form */}
                    <Card title="Add Comment" size="small">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Alert
                                message="Internal notes are only visible to admins and will not be sent to the customer"
                                type="info"
                                showIcon
                                icon={<EyeInvisibleOutlined />}
                                style={{ marginBottom: 8 }}
                            />
                            <TextArea
                                rows={4}
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder="Type your comment here..."
                            />
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Space>
                                    <Button
                                        type={isInternal ? 'default' : 'primary'}
                                        icon={<EyeOutlined />}
                                        onClick={() => setIsInternal(false)}
                                    >
                                        Public Reply
                                    </Button>
                                    <Button
                                        type={isInternal ? 'primary' : 'default'}
                                        icon={<EyeInvisibleOutlined />}
                                        onClick={() => setIsInternal(true)}
                                        danger={isInternal}
                                    >
                                        Internal Note
                                    </Button>
                                    <Upload
                                        beforeUpload={handleFileUpload}
                                        showUploadList={false}
                                        disabled={uploading}
                                    >
                                        <Button icon={<PaperClipOutlined />} loading={uploading}>
                                            Attach File
                                        </Button>
                                    </Upload>
                                </Space>
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={handleAddComment}
                                    disabled={!commentText.trim()}
                                >
                                    Send
                                </Button>
                            </Space>
                        </Space>
                    </Card>
                </Space>
                )}
            </Spin>

            {/* Status Change Reason Modal */}
            <Modal
                title={`${pendingStatus === 'resolved' ? 'Resolve' : 'Close'} Ticket`}
                open={statusChangeModalVisible}
                onOk={handleStatusChangeConfirm}
                onCancel={handleStatusChangeCancel}
                okText={pendingStatus === 'resolved' ? 'Resolve Ticket' : 'Close Ticket'}
                cancelText="Cancel"
                width={500}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                        Please provide a {pendingStatus === 'resolved' ? 'resolution note' : 'closure reason'}:
                    </Text>
                    <TextArea
                        rows={4}
                        value={statusChangeReason}
                        onChange={(e) => setStatusChangeReason(e.target.value)}
                        placeholder={
                            pendingStatus === 'resolved'
                                ? 'Explain how the issue was resolved...'
                                : 'Explain why the ticket is being closed...'
                        }
                        autoFocus
                    />
                </Space>
            </Modal>
        </Drawer>
    );
};
